import api from '../axios';
import type { BillingInterval, DePlan, DiscountAppliesTo } from '@/lib/pricing';
import { discountTargetSuffix, formatUSD } from '@/lib/pricing';

export type { DiscountAppliesTo };

const BASE = '/api/billing';

/** Server message from a billing call, including Nest's string-or-array `message`. */
export function billingErrorMessage(error: unknown, fallback: string): string {
  const message =
    error && typeof error === 'object' && 'message' in error
      ? (error as { message?: unknown }).message
      : undefined;
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (Array.isArray(message)) {
    const first = message.find(
      (item): item is string => typeof item === 'string' && !!item.trim(),
    );
    if (first) return first.trim();
  }
  return fallback;
}

/** Pulls the server's message out of an error whose body came back as a Blob. */
async function messageFromBlobError(err: unknown): Promise<string> {
  const fallback = 'That invoice could not be downloaded. Please try again.';
  // The shared interceptor rejects with { status, message, originalError },
  // so the axios response lives one level down.
  const wrapped = err as { originalError?: { response?: { data?: unknown } } } | undefined;
  const body = wrapped?.originalError?.response?.data;
  if (!(body instanceof Blob)) return fallback;
  try {
    const parsed = JSON.parse(await body.text()) as { message?: string };
    return parsed.message || fallback;
  } catch {
    return fallback;
  }
}

/** One Stripe invoice, as the billing settings screen lists it. */
export interface InvoiceSummary {
  id: string;
  /** Human invoice number. Null only while Stripe has not finalised it. */
  number: string | null;
  /** 'open' | 'paid' | 'uncollectible' | 'void'. Drafts are never listed. */
  status: string;
  /** Amounts in the currency's smallest unit, as Stripe reports them. */
  total: number;
  /** Credit notes against this invoice. Non-zero means refunded, wholly or partly. */
  creditedAmount: number;
  currency: string;
  issuedAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  hasPdf: boolean;
}

/** Mirrors CreateCheckoutSessionDto on the backend. */
export interface CheckoutSelection {
  fpaSeats: number;
  de?: boolean;
  dePlan?: DePlan;
  employees?: number;
  integrations?: number;
  /** 'year' bills annually at 20% off (baked into the annual Stripe prices). Default 'month'. */
  interval?: BillingInterval;
  /** The actual integrations the buyer picked (presets + custom). Persisted in the saved plan. */
  integrationNames?: string[];
  /**
   * Custom (admin-priced) plan. When true the plan is a single all-in monthly
   * price set by an admin; the standard products/quote do not apply. The price
   * itself is UI-only here: checkout re-reads it server-side from the stored
   * plan and never takes it from the request. At organisation scope the stored
   * plan also decides custom vs standard, and a flag that disagrees is
   * rejected (409).
   */
  custom?: boolean;
  /** Admin-entered all-in monthly price in whole USD (display; server re-reads it). */
  customPriceMonthly?: number;
  /** Admin-entered plan name shown to the user (e.g. "Enterprise"). */
  customName?: string;
  /** Short admin-entered description of what the custom plan includes. */
  customLabel?: string;
  /** ISO timestamp set when the buyer validates the pricing agreement. */
  termsAcceptedAt?: string;
  /** Customer legal / company name captured on the pricing-agreement step. */
  companyName?: string;
  /** Full name of the person validating the agreement. */
  signerName?: string;
  /**
   * The discount the buyer signed (from the preview/signed agreement). Checkout
   * rejects (409) if the current server-side discount no longer matches, so the
   * charge can't diverge from the signed contract. Not persisted in the plan query.
   */
  expectedDiscountPercent?: number | null;
  /** The intro-discount schedule the buyer signed — same 409 guard semantics. */
  expectedDiscountSchedule?: DiscountStep[] | null;
  /** What the signed discount applied to — same 409 guard semantics. */
  expectedDiscountAppliesTo?: DiscountAppliesTo;
  /**
   * The one-time fee the buyer signed, in cents. Sent only while the
   * organisation owes one: checkout then rejects (409) a missing or different
   * value, and also rejects it when no fee is owed. Not persisted in the plan query.
   */
  expectedSetupFeeCents?: number;
  /** How many installments the signed fee is paid in. Same 409 guard semantics. */
  expectedSetupFeeInstallments?: number;
}

/** One intro-discount step: billing month N (1-based) gets `percent` off. */
export interface DiscountStep {
  month: number;
  percent: number;
}

/**
 * Human summary of a discount, e.g.
 * "50% off month 1, 20% off month 2, then 10% off" or "10% off".
 * Empty string when there is no discount at all.
 */
export function discountSummaryText(
  schedule: DiscountStep[] | null | undefined,
  ongoingPercent: number | null | undefined,
  appliesTo?: DiscountAppliesTo | null,
): string {
  const on = discountTargetSuffix(appliesTo);
  const intro = (schedule ?? [])
    .slice()
    .sort((a, b) => a.month - b.month)
    .map((s) => `${s.percent}% off month ${s.month}`)
    .join(', ');
  if (intro && ongoingPercent) return `${intro}, then ${ongoingPercent}% off${on}`;
  if (intro) return `${intro}${on}, then standard price`;
  if (ongoingPercent) return `${ongoingPercent}% off${on}`;
  return '';
}

/** Name of a one-time fee the admin left unnamed. Mirrors the backend default. */
export const DEFAULT_SETUP_FEE_LABEL = 'Implementation fee';

/** Highest one-time fee an admin can set, in whole USD. */
export const MAX_SETUP_FEE_USD = 999_999;

/** Most monthly installments a one-time fee can be paid in. */
export const MAX_SETUP_FEE_INSTALLMENTS = 12;

/**
 * Cents of each installment of a one-time fee, first to last. Amounts are
 * whole dollars: every installment but the last is the total divided evenly
 * and rounded down, and the last one takes the rest ($5,000 in 3 is $1,666,
 * $1,666 and $1,668). Mirrors the backend split.
 */
export function setupFeeInstallmentAmountsCents(amountCents: number, installments: number): number[] {
  const count = Math.max(1, Math.floor(installments));
  const totalDollars = Math.round(amountCents / 100);
  const base = Math.floor(totalDollars / count);
  return Array.from({ length: count }, (_, i) => (i < count - 1 ? base : totalDollars - base * (count - 1)) * 100);
}

/**
 * "2 monthly installments of $2,500", or "3 monthly installments of $1,666
 * (the last one $1,668)" when the split leaves a remainder.
 */
export function setupFeeInstallmentsText(amountsCents: number[]): string {
  const first = amountsCents[0] ?? 0;
  const last = amountsCents[amountsCents.length - 1] ?? first;
  const remainder = last !== first ? ` (the last one ${formatUSD(last / 100)})` : '';
  return `${amountsCents.length} monthly installments of ${formatUSD(first / 100)}${remainder}`;
}

/** Serialize a selection into a query string for the /checkout/confirm review page. */
export function selectionToQuery(sel: CheckoutSelection): string {
  const p = new URLSearchParams();
  // A custom plan is standalone: the admin-set price IS the plan, so none of
  // the standard product keys ride along.
  if (sel.custom) {
    p.set('custom', '1');
    // No customPrice key while the price is unset (admin hasn't priced it yet)
    // — writing one would turn "price pending" into a fake $1 plan.
    if ((sel.customPriceMonthly ?? 0) >= 1) {
      p.set('customPrice', String(Math.round(sel.customPriceMonthly ?? 0)));
    }
    if (sel.customName?.trim()) p.set('customName', sel.customName.trim());
    if (sel.customLabel?.trim()) p.set('customLabel', sel.customLabel.trim());
    return p.toString();
  }
  p.set('fpaSeats', String(sel.fpaSeats));
  if (sel.de) p.set('de', '1');
  if (sel.dePlan) p.set('dePlan', sel.dePlan);
  if (sel.employees != null) p.set('employees', String(sel.employees));
  if (sel.integrations != null) p.set('integrations', String(sel.integrations));
  if (sel.interval === 'year') p.set('interval', 'year');
  // Persist each chosen integration by name (repeated key, URL-encoded).
  for (const name of sel.integrationNames ?? []) p.append('integration', name);
  return p.toString();
}

/** Parse a selection back out of URL search params on the review page. */
export function selectionFromParams(params: URLSearchParams): CheckoutSelection {
  if (params.get('custom') === '1') {
    return {
      fpaSeats: 1, // unused for custom plans; kept so callers never see 0
      custom: true,
      customPriceMonthly: Math.max(0, parseInt(params.get('customPrice') || '0', 10) || 0),
      customName: params.get('customName') ?? '',
      customLabel: params.get('customLabel') ?? '',
    };
  }
  return {
    fpaSeats: Math.max(1, parseInt(params.get('fpaSeats') || '1', 10) || 1),
    de: params.get('de') === '1',
    dePlan: params.get('dePlan') === 'UNLIMITED' ? 'UNLIMITED' : 'STARTER',
    employees: parseInt(params.get('employees') || '0', 10) || 0,
    integrations: parseInt(params.get('integrations') || '0', 10) || 0,
    integrationNames: params.getAll('integration'),
    interval: params.get('interval') === 'year' ? 'year' : 'month',
  };
}

export interface CheckoutSession {
  url: string;
  sessionId: string;
}

/**
 * The one-time fee an admin set on the buyer's organisation, as the status
 * reports it. Charged with the first payment (or split over the first monthly
 * payments) and never discounted.
 */
export interface SubscriptionSetupFee {
  /** Whole fee in cents. */
  amountCents: number;
  /** Display name, default already applied (e.g. "Implementation fee"). */
  label: string;
  /** 1 = in full with the first payment; 2 to 12 = monthly installments. */
  installments: number;
  /** Cents of each installment, first to last (one entry when paid in full). */
  installmentAmountsCents: number[];
  /** Installments paid so far, in the Stripe mode the server runs on. */
  installmentsPaid: number;
  /** When the last installment was paid, in that same mode. */
  paidAt: string | null;
  /**
   * The next checkout charges it: nothing is paid yet and the organisation has
   * no live subscription. Sign and checkout must then echo it back.
   */
  due: boolean;
}

/** Read-only subscription + Plus-join status for the current user/org. */
export interface SubscriptionStatus {
  active: boolean;
  status: string | null;
  seats: number | null;
  employees: number | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** Has the user joined Blueberries Plus (pre-payment)? */
  joined: boolean;
  /** Saved plan as a checkout query string, if any. */
  plan: string | null;
  /** Company name from signup, to prefill the Plus agreement. */
  companyName?: string | null;
  /** Signer full name (first + last from signup), to prefill the Plus agreement. */
  signerName?: string | null;
  /** Admin-granted ONGOING discount (the org's, else this user's; whole percent 1–99), or null. */
  discountPercent?: number | null;
  /** Admin-granted intro-months discount schedule (e.g. 50% month 1, 20% month 2), or null. */
  discountSchedule?: DiscountStep[] | null;
  /** What the discount reduces; absent from an older API = 'subscription'. */
  discountAppliesTo?: DiscountAppliesTo;
  /** Whitelisted flow-demo account: may un-join Plus to re-run the join flow. */
  canResetPlus?: boolean;
  /**
   * An admin assigned this (already-subscribed) user a NEW plan awaiting their
   * acceptance. Accepting pays a new subscription and the old one is canceled
   * automatically by the webhook.
   */
  offerPending?: boolean;
  /**
   * The organisation's one-time fee, or null when none is set (always null
   * for terms stored on a user). Absent from an older API.
   */
  setupFee?: SubscriptionSetupFee | null;
}

/** Mirrors AgreementPreviewDto on the backend. */
export interface AgreementPreviewPayload {
  companyName?: string;
  signerName?: string;
  /** Human-readable plan line items, e.g. "FP&A License: 1 seat, $1,500.00 / month". */
  planLines: string[];
  /** Formatted monthly total (pre-discount), e.g. "$1,545.00". */
  monthlyTotal: string;
  /** Numeric monthly total (pre-discount) so the server can apply the discount. */
  monthlyTotalAmount: number;
  /** Data Enrichment part of monthlyTotalAmount, for a Data Enrichment-only discount. */
  dataEnrichmentAmount?: number;
}

/** The rendered Plus pricing agreement (same shape the /sign page consumes). */
export interface AgreementPreview {
  title: string;
  bodyHtml: string;
  /** The date merged into the render; echoed back when signing so stored == shown. */
  documentDate: string;
  /** Formatted ongoing total the buyer pays (post-discount), e.g. "$1,236.00". */
  monthlyTotal: string;
  /** Applied ongoing discount, or null. */
  discountPercent: number | null;
  /** Applied intro-months schedule, or null. */
  discountSchedule?: DiscountStep[] | null;
  /** What the applied discount reduces. */
  discountAppliesTo?: DiscountAppliesTo;
}

/** Mirrors SignAgreementDto on the backend. */
export interface SignAgreementPayload {
  companyName: string;
  signerName: string;
  planLines: string[];
  monthlyTotal: string;
  /** Numeric monthly total (pre-discount) so the server applies the discount. */
  monthlyTotalAmount: number;
  /** Data Enrichment part of monthlyTotalAmount, for a Data Enrichment-only discount. */
  dataEnrichmentAmount?: number;
  /** The selected plan as a checkout query string, for reference. */
  plan?: string;
  /** documentDate from the previewed render, so the stored document matches the screen. */
  documentDate?: string;
  /**
   * The discount the buyer reviewed (from the preview response). Sign rejects
   * (409) if the current server-side discount no longer matches.
   */
  expectedDiscountPercent?: number | null;
  /** The intro schedule the buyer reviewed — same 409 guard semantics. */
  expectedDiscountSchedule?: DiscountStep[] | null;
  /** What the reviewed discount applied to — same 409 guard semantics. */
  expectedDiscountAppliesTo?: DiscountAppliesTo;
  /**
   * The one-time fee the buyer reviewed, in cents. Sent only while the
   * organisation owes one: sign then rejects (409) a missing or different
   * value, and also rejects it when no fee is owed.
   */
  expectedSetupFeeCents?: number;
  /** How many installments the reviewed fee is paid in. Same 409 guard semantics. */
  expectedSetupFeeInstallments?: number;
}

/** A user's admin-granted discount: ongoing percent + intro-months schedule. */
export interface UserDiscount {
  percentOff: number | null;
  schedule?: DiscountStep[] | null;
  appliesTo?: DiscountAppliesTo;
  /** 'organization' when the user belongs to an org: the discount is the org's. */
  scope?: BillingTermsScope;
  organizationId?: string | null;
}

/**
 * Where a plan and discount live. 'organization': the org's own terms, shared
 * by every member. 'user': the legacy per-user terms (orgless buyers, and orgs
 * no admin has configured yet).
 */
export type BillingTermsScope = 'organization' | 'user';

/**
 * An organisation's one-time fee as stored on its own plan row. Mirrors
 * OrganizationSetupFeeRecord on the backend.
 */
export interface OrganizationSetupFee {
  /** Whole fee in cents (whole dollars only). */
  amountCents: number;
  /** Admin-entered name; null means DEFAULT_SETUP_FEE_LABEL. */
  label: string | null;
  /** 1 = in full with the first payment; 2 to 12 = monthly installments. */
  installments: number;
  /** Installments recorded as paid, 0 to `installments`. */
  installmentsPaid: number;
  /**
   * When the last installment was paid. A date stamped by hand, with no
   * installment count, also means the fee is paid in full.
   */
  paidAt: string | null;
  /** Stripe invoice of the first payment. */
  paidInvoiceId: string | null;
  /** Stripe mode the payments were recorded in (false = test mode). */
  paidLivemode: boolean | null;
}

/** An organisation's plan and discount, as the admin screens read them. */
export interface OrganizationBillingPlan {
  scope: BillingTermsScope;
  organizationId: string | null;
  plan: string | null;
  percentOff: number | null;
  schedule: DiscountStep[] | null;
  appliesTo?: DiscountAppliesTo;
  offerPending: boolean;
  /** The organisation's one-time fee, or null. Absent from an older API. */
  setupFee?: OrganizationSetupFee | null;
  /** The organisation already pays (active, trialing or past due). */
  hasLiveSubscription?: boolean;
  /** The live pricing agreement has a section shown only when a one-time fee applies. */
  setupFeeClausePublished?: boolean;
}

export interface SignAgreementResult {
  signedAt: string;
  documentHash: string;
  agreementVersion: string;
}

/** A stored signed Plus agreement (admin detail view). */
export interface PlusAgreementSignature {
  /** Row id (handle for the per-signature PDF route). */
  id?: string;
  userId: string;
  userEmail: string | null;
  organizationId: string | null;
  companyName: string;
  signerName: string;
  plan: string | null;
  planLines: string[];
  monthlyTotal: string | null;
  agreementVersion: string;
  documentHash: string;
  documentHtml: string;
  signatureIp: string | null;
  signedAt: string;
}

/** One stored signature in the admin "signed agreements" list (no document body). */
export type PlusAgreementSignatureSummary = Omit<PlusAgreementSignature, 'documentHtml'> & {
  id: string;
  /** Human title derived from the version family (free integration / pricing). */
  title: string;
};

/** Everything a user has agreed to, for the admin user-detail panel. */
export interface UserAgreements {
  msa: {
    acceptedAt: string | null;
    version: string | null;
    documentHash: string | null;
    signupIp: string | null;
  } | null;
  /** Every MSA acceptance on record, newest first. */
  msaHistory: Array<{ version: string; acceptedAt: string; documentHash: string | null; clientIp: string | null; method: string }>;
  signatures: Array<PlusAgreementSignatureSummary & { agreementKey: string | null; isCurrent: boolean }>;
  /** Version currently live per agreement key. */
  live: Record<string, string>;
}

/**
 * GET an authenticated PDF and hand it to the browser as a download. Shared by
 * the invoice and agreement routes: the bytes come through our API (JWT), so
 * the browser gets a normal authenticated response rather than a bare link.
 */
export async function downloadPdf(url: string, fallbackName: string): Promise<void> {
  let res;
  try {
    res = await api.get(url, { responseType: 'blob' });
  } catch (err) {
    // With responseType 'blob' an error body arrives as a Blob, so the axios
    // interceptor cannot read the message out of it. Unwrap it here, or the
    // carefully written server message never reaches the user.
    throw new Error(await messageFromBlobError(err));
  }
  const disposition = res.headers['content-disposition'] as string | undefined;
  const match = disposition?.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] ?? fallbackName;
  const objectUrl = URL.createObjectURL(res.data);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoking synchronously can cancel a download the browser has not started yet.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}

export const billingApi = {
  /** Creates a Stripe-hosted Checkout Session and returns its URL. */
  createCheckoutSession: (selection: CheckoutSelection): Promise<CheckoutSession> => {
    // integrationNames is UI-only (the names ride the plan query saved at sign;
    // the checkout DTO only accepts the integration COUNT). Drop it before POST,
    // or the backend's forbidNonWhitelisted validation rejects the request.
    // Custom price, name and label are UI-only too: the server re-reads them
    // from the stored plan (the organisation's own plan, else the buyer's
    // plus_signups row), so only the `custom` flag is sent.
    const payload = { ...selection };
    delete payload.integrationNames;
    delete payload.customPriceMonthly;
    delete payload.customName;
    delete payload.customLabel;
    return api.post(`${BASE}/checkout`, payload).then((r) => r.data?.data ?? r.data);
  },

  /** Current subscription + Plus-join status for the signed-in user. */
  getSubscription: (): Promise<SubscriptionStatus> =>
    api.get(`${BASE}/subscription`).then((r) => r.data?.data ?? r.data),

  /**
   * Stripe Billing Portal session: self-manage the subscription (payment
   * method, invoices, cancel). Redirect to the returned url.
   */
  createPortalSession: (): Promise<{ url: string }> =>
    api.post(`${BASE}/portal`, {}).then((r) => r.data?.data ?? r.data),

  /**
   * The signed-in user's organisation's invoices, newest first. The backend
   * derives the organisation from the token, so nothing is passed here.
   */
  listInvoices: (): Promise<{ invoices: InvoiceSummary[]; hasMore: boolean }> =>
    api.get(`${BASE}/invoices`).then((r) => r.data?.data ?? r.data),

  /**
   * Downloads one invoice PDF. The bytes come through our API rather than a
   * Stripe link, so the browser gets a normal authenticated response.
   */
  downloadInvoicePdf: (invoice: Pick<InvoiceSummary, 'id' | 'number'>): Promise<void> =>
    downloadPdf(
      `${BASE}/invoices/${encodeURIComponent(invoice.id)}/pdf`,
      `invoice-${invoice.number ?? invoice.id}.pdf`,
    ),

  /**
   * Schedules the organisation's subscription to stop at the end of the paid
   * period. No subscription id is sent: the backend resolves it from the token.
   *
   * DELIBERATELY NOT WIRED TO ANY BUTTON YET. The route exists and is guarded,
   * but it has never run against a real subscription: this Stripe account has
   * no test key, so its first real execution would be a paying customer
   * clicking cancel. Until that round trip has been exercised, the UI hands
   * cancellation to the Stripe portal instead. See ManageSubscriptionDialog.
   */
  cancelSubscription: (reason?: string): Promise<{ cancelAtPeriodEnd: true }> =>
    api.post(`${BASE}/cancel`, reason ? { reason } : {}).then((r) => r.data?.data ?? r.data),

  /** Undoes a scheduled cancellation. */
  resumeSubscription: (): Promise<{ cancelAtPeriodEnd: false }> =>
    api.post(`${BASE}/resume`, {}).then((r) => r.data?.data ?? r.data),

  /** Marks the signed-in user as having joined Blueberries Plus. */
  joinPlus: (plan: string): Promise<{ joined: true }> =>
    api.post(`${BASE}/join-plus`, { plan }).then((r) => r.data?.data ?? r.data),

  /** Flow-demo only: un-joins Plus to re-run the join flow (403 otherwise). */
  resetPlusJoin: (): Promise<{ reset: true }> =>
    api.post(`${BASE}/reset-plus-join`, {}).then((r) => r.data?.data ?? r.data),

  /** Renders the Plus pricing agreement with the buyer's plan + names merged in. */
  getAgreementPreview: (payload: AgreementPreviewPayload): Promise<AgreementPreview> =>
    api.post(`${BASE}/agreement-preview`, payload).then((r) => r.data?.data ?? r.data),

  /** The generic Blueberries Plus pricing terms (same for all companies). Public. */
  getPlusPricingTerms: (): Promise<{ title: string; bodyHtml: string }> =>
    api.get(`${BASE}/plus-pricing-terms`).then((r) => r.data?.data ?? r.data),

  /** Records the signed Plus agreement (who/when/what) before checkout. */
  signPlusAgreement: (payload: SignAgreementPayload): Promise<SignAgreementResult> =>
    api.post(`${BASE}/agreement/sign`, payload).then((r) => r.data?.data ?? r.data),

  /**
   * The generic Blueberries Plus Free Integration terms (same for all
   * companies). Public. The core route is still named pilot-terms (it predates
   * the rename from "Free Pilot").
   */
  getIntegrationTerms: (): Promise<{ title: string; bodyHtml: string }> =>
    api.get(`${BASE}/pilot-terms`).then((r) => r.data?.data ?? r.data),

  /** Records the accepted Free Integration Agreement (who/when/what) before joining Plus. */
  signPilotAgreement: (payload: SignAgreementPayload): Promise<SignAgreementResult> =>
    api.post(`${BASE}/agreement/pilot/sign`, payload).then((r) => r.data?.data ?? r.data),

  /** Latest signed Plus agreement for a user (ADMIN only). Null when unsigned. */
  getPlusAgreementSignature: (userId: string): Promise<PlusAgreementSignature | null> =>
    // data:null is a legitimate 200 here (user never signed) — do NOT fall back
    // to the response envelope like the other endpoints' unwrap does.
    api.get(`${BASE}/agreement-signatures/${userId}`).then((r) => r.data?.data ?? null),

  /** Everything a user has agreed to: MSA evidence + every Plus signature (ADMIN only). */
  listUserAgreements: (userId: string): Promise<UserAgreements> =>
    api.get(`${BASE}/agreement-signatures/${userId}/all`).then((r) => r.data?.data ?? r.data),

  /** Downloads one signed Plus agreement (free integration or pricing) as a PDF (ADMIN only). */
  downloadAgreementPdf: (userId: string, signatureId: string, fallbackName: string): Promise<void> =>
    downloadPdf(
      `${BASE}/agreement-signatures/${encodeURIComponent(userId)}/${encodeURIComponent(signatureId)}/pdf`,
      fallbackName,
    ),

  /** Downloads the MSA the user accepted, with acceptance evidence, as a PDF (ADMIN only). */
  downloadMsaPdf: (userId: string, version?: string): Promise<void> =>
    downloadPdf(
      `${BASE}/users/${encodeURIComponent(userId)}/msa/pdf${version ? `?version=${encodeURIComponent(version)}` : ''}`,
      'master-services-agreement.pdf',
    ),

  /** A user's admin-granted overall discount (ADMIN only). */
  getUserDiscount: (userId: string): Promise<UserDiscount> =>
    api.get(`${BASE}/users/${userId}/discount`).then((r) => r.data?.data ?? r.data),

  /** A user's saved plan query string, with the integrations they chose (ADMIN only). */
  getUserPlan: (
    userId: string,
  ): Promise<{ plan: string | null; scope?: BillingTermsScope; organizationId?: string | null }> =>
    api.get(`${BASE}/users/${userId}/plan`).then((r) => r.data?.data ?? r.data),

  /**
   * Sets or clears a user's discount (ADMIN only). `percentOff` is the ongoing
   * percent (1–99 or null); `schedule` the intro months (consecutive from
   * month 1) — e.g. [{month:1,percent:50},{month:2,percent:20}] — or null.
   */
  setUserDiscount: (
    userId: string,
    percentOff: number | null,
    schedule: DiscountStep[] | null = null,
    appliesTo: DiscountAppliesTo = 'subscription',
  ): Promise<UserDiscount> =>
    api
      .put(`${BASE}/users/${userId}/discount`, { percentOff, schedule, appliesTo })
      .then((r) => r.data?.data ?? r.data),

  /** Assigns a Plus plan (checkout query string) to a user — pre-fills their Plus page (ADMIN only). */
  setUserPlan: (userId: string, plan: string): Promise<{ plan: string }> =>
    api.put(`${BASE}/users/${userId}/plan`, { plan }).then((r) => r.data?.data ?? r.data),

  /** Emails a user a link to their pre-filled Plus page ("your plan is ready") (ADMIN only). */
  sendPlusOffer: (userId: string): Promise<{ sent: boolean }> =>
    api.post(`${BASE}/users/${userId}/send-plus-offer`, {}).then((r) => r.data?.data ?? r.data),

  /**
   * Every Plus agreement signed for an organisation (implementation and
   * pricing), newest first, including those its members signed before the org
   * existed (ADMIN only).
   */
  getOrganizationAgreementSignatures: (organizationId: string): Promise<PlusAgreementSignature[]> =>
    api
      .get(`${BASE}/organizations/${organizationId}/agreement-signatures`)
      .then((r) => (r.data?.data ?? []) as PlusAgreementSignature[]),

  /** An organisation's plan and discount, shared by every member (ADMIN only). */
  getOrganizationPlan: (organizationId: string): Promise<OrganizationBillingPlan> =>
    api.get(`${BASE}/organizations/${organizationId}/plan`).then((r) => r.data?.data ?? r.data),

  /** Assigns an organisation's Plus plan (checkout query string) (ADMIN only). */
  setOrganizationPlan: (organizationId: string, plan: string): Promise<OrganizationBillingPlan> =>
    api
      .put(`${BASE}/organizations/${organizationId}/plan`, { plan })
      .then((r) => r.data?.data ?? r.data),

  /** Sets or clears an organisation's discount, same shape as setUserDiscount (ADMIN only). */
  setOrganizationDiscount: (
    organizationId: string,
    percentOff: number | null,
    schedule: DiscountStep[] | null = null,
    appliesTo: DiscountAppliesTo = 'subscription',
  ): Promise<OrganizationBillingPlan> =>
    api
      .put(`${BASE}/organizations/${organizationId}/discount`, { percentOff, schedule, appliesTo })
      .then((r) => r.data?.data ?? r.data),

  /**
   * Sets an organisation's one-time fee, or clears it with `amountCents` null
   * (ADMIN only). `amountCents` is whole dollars in cents ($1 to $999,999);
   * `installments` is 1 (in full with the first payment) to 12. Refused once
   * an installment is paid, or while the organisation already pays.
   */
  setOrganizationSetupFee: (
    organizationId: string,
    fee: { amountCents: number | null; label?: string | null; installments?: number },
  ): Promise<OrganizationBillingPlan> =>
    api
      .put(`${BASE}/organizations/${organizationId}/setup-fee`, fee)
      .then((r) => r.data?.data ?? r.data),

  /** Emails every admin of the organisation its pre-filled Plus page (ADMIN only). */
  sendOrganizationPlusOffer: (organizationId: string): Promise<{ sent: number }> =>
    api
      .post(`${BASE}/organizations/${organizationId}/send-plus-offer`, {})
      .then((r) => r.data?.data ?? r.data),
};
